import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import React from 'react';

interface ErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
  onReload?: () => void;
}

export const ErrorFallback = React.memo(function ErrorFallback({
  error,
  onRetry,
  onReload,
}: ErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center p-6 min-h-[200px] bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-8 h-8 text-red-500" />
        <h2 className="text-xl font-semibold text-red-800">Something went wrong</h2>
      </div>
      <p className="text-red-700 text-center mb-6 max-w-md">
        An unexpected error occurred. Please try again. If the problem persists, reload the page.
      </p>
      {process.env.NODE_ENV === 'development' && error && (
        <details className="mb-6 p-4 bg-red-100 rounded border border-red-300 max-w-2xl w-full">
          <summary className="cursor-pointer font-medium text-red-800 mb-2">
            Error Details (Development)
          </summary>
          <pre className="text-sm text-red-700 whitespace-pre-wrap overflow-auto">
            {error.name}: {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </details>
      )}
      <div className="flex gap-3">
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Try Again
        </button>
        {onReload && (
          <button
            onClick={onReload}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reload Page
          </button>
        )}
      </div>
    </div>
  );
});
